import React, {useState} from 'react';
import styled from 'styled-components';
import {Button, Checkbox, FlexColumn, SearchField, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {ValueSource} from './valueSources/valueSource.types';

const tokens = tokensForThemeProvider.sky.sys;

export interface ConditionValuePickerProps {
	source: ValueSource;
	selectedIds: string[];
	onChange: (ids: string[]) => void;
	/** Rendered above the search field. Null (or omitted) when the field offers no operator choice. */
	operatorPicker?: JSX.Element | null;
}

/** `DropdownList.Content` gives its content vertical padding only (zero horizontal) — every
 * built-in row type (`DropdownList.Item`, its context-group headers) supplies its own left/right
 * padding. This picker renders a bare `SearchField` and `Checkbox` list instead of
 * `DropdownList.Item`s, so without its own horizontal padding they'd render flush against the
 * dropdown's edges. */
const PickerWrapper = styled(FlexColumn)`
	padding: 0 ${tokens.spacing['400']};
`;

/** Search + checkbox multi-select over a `ValueSource`'s candidates. The checkbox list is fully
 * driven by `source.useCandidates(search)` — this component holds no candidate state of its own,
 * only the local search text. */
export function ConditionValuePicker({source, selectedIds, onChange, operatorPicker}: ConditionValuePickerProps): JSX.Element {
	const {formatMessage} = useIntl();
	const [search, setSearch] = useState('');
	const {items, loading, error, retry} = source.useCandidates(search);

	// Everything already picked stays on the list, whatever the search says.
	//
	// A candidate list is a search result, so a selected value drops off it as soon as the search
	// stops matching. On a picker whose candidates are typed rather than fetched -- a batch id --
	// that happens on the very next keystroke: type a second id and the first becomes a ticked value
	// with no checkbox to untick. The only way back was to delete the whole condition. The same is
	// true of every fetched source, just less often.
	//
	// Selected entries lead, in selection order, so the list does not reorder under the reader as
	// they type. Labels come from `resolveLabels`, which is what the tile summary already renders
	// them with, so a value reads the same in both places.
	const selectedLabels = source.resolveLabels(selectedIds);
	const shown = [
		...selectedIds.map(id => ({id, label: selectedLabels[id] ?? id})),
		...items.filter(item => !selectedIds.includes(item.id)),
	];

	const toggle = (id: string, checked: boolean): void => {
		if (source.singleSelect) {
			onChange(checked ? [id] : []);
			return;
		}
		onChange(checked ? [...selectedIds, id] : selectedIds.filter(x => x !== id));
	};

	return (
		<PickerWrapper gap={'s'}>
			{operatorPicker}
			<SearchField
				value={search}
				onChange={setSearch}
				isExpandable={false}
				placeholder={formatMessage({id: 'common.search'})}
				data-cy={'ai-report-condition-value-search'}
			/>
			{loading ? (
				<Text color={'medium'}>
					<FormattedMessage id={'ai_report_builder.conditions.value_picker_loading'} />
				</Text>
			) : error ? (
				<FlexColumn gap={'xs'} data-cy={'ai-report-condition-value-error'}>
					<Text color={'medium'}>
						<FormattedMessage id={'ai_report_builder.conditions.value_picker_error'} />
					</Text>
					<Button emphasis={'low'} onPress={() => retry?.()} data-cy={'ai-report-condition-value-retry'}>
						<FormattedMessage id={'common.retry'} />
					</Button>
				</FlexColumn>
			) : (
				<FlexColumn gap={'xs'}>
					{shown.length === 0 ? (
						<Text color={'medium'}>
							<FormattedMessage id={'ai_report_builder.conditions.value_picker_empty'} />
						</Text>
					) : (
						shown.map(item => (
							<Checkbox
								key={item.id}
								checked={selectedIds.includes(item.id)}
								onChange={checked => toggle(item.id, checked === true)}
								labelText={item.label}
								data-cy={`ai-report-condition-value-${item.id}`}
							/>
						))
					)}
				</FlexColumn>
			)}
		</PickerWrapper>
	);
}
